import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/soul";
import { Feather, ArrowRight } from "lucide-react";

export default function PowerOfWords() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/power-of-words").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="min-h-[60vh] grid place-items-center"><span className="serif text-3xl text-[#D4AF37] slow-pulse">✦</span></div>;

  return (
    <main data-testid="power-of-words-page">
      <section className="max-w-5xl mx-auto px-6 pt-20 pb-8">
        <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-5 flex items-center gap-2 fade-up">
          <Feather size={14} /> The Power of Words
        </p>
        <h1 className="serif text-4xl sm:text-6xl lg:text-7xl font-light text-[#F5F1E7] leading-[1.05] fade-up">
          In the beginning <span className="italic-serif text-[#D4AF37]">was the Word.</span>
        </h1>
        <p className="mt-8 text-lg text-[#A8A29E] leading-relaxed max-w-3xl">{data.intro}</p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link
            to="/words"
            data-testid="pow-to-explorer"
            className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#110D14] px-7 py-3 font-medium hover:bg-[#e6c860] transition-colors duration-300"
          >
            Explore a word's origins
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <Link
            to="/roots"
            data-testid="pow-to-roots"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] px-7 py-3 hover:bg-[#D4AF37]/10 transition-colors duration-300"
          >
            Learn a root a day
          </Link>
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="serif text-3xl sm:text-4xl text-[#F5F1E7] mb-10">
          Why origins <span className="italic-serif text-[#D4AF37]">matter</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-6" data-testid="pow-principles">
          {data.principles.map((p, i) => (
            <div key={i} className="glass rounded-2xl p-8">
              <div className="serif text-4xl text-[#D4AF37]/40">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="serif text-2xl text-[#F5F1E7] mt-2">{p.title}</h3>
              <p className="mt-3 text-[#A8A29E] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="serif text-3xl sm:text-4xl text-[#F5F1E7] mb-3">
          When the study of word-origins <span className="italic-serif text-[#C1272D]">left the schools</span>
        </h2>
        <p className="text-[#A8A29E] leading-relaxed mb-12 max-w-2xl">
          How the teaching of etymology — Latin and Greek roots, and the phonics that reveal a word's
          structure — quietly faded from mainstream American education.
        </p>

        <div className="relative border-l border-white/10 ml-3" data-testid="pow-timeline">
          {data.timeline.map((t, i) => (
            <div key={i} className="relative pl-8 pb-12 last:pb-0" data-testid={`timeline-${i}`}>
              <span
                className="absolute -left-[7px] top-1.5 h-3.5 w-3.5 rounded-full border-2"
                style={{ borderColor: "#D4AF37", background: "#110D14" }}
              />
              <p className="text-[#D4AF37] tracking-wide text-sm font-medium">{t.year}</p>
              <h3 className="serif text-2xl text-[#F5F1E7] mt-1">{t.title}</h3>
              <p className="mt-2 text-[#A8A29E] leading-relaxed">{t.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 glass rounded-2xl p-7 border-l-2" style={{ borderLeftColor: "#C1272D" }} data-testid="pow-history-note">
          <p className="text-xs uppercase tracking-[0.25em] text-[#A8A29E] mb-2">An honest note</p>
          <p className="text-[#F5F1E7] leading-relaxed italic-serif text-lg">{data.history_note}</p>
        </div>
      </section>
    </main>
  );
}
